import * as styles from '../styles/containers/Application.less';
import React, { useEffect } from 'react';
import { I18nextProvider, withTranslation, WithTranslation } from "react-i18next";
import i18n from "../services/i18n";
import Header from '../components/common/Header';
import { useActions } from '../hooks/useActions';
import { useAppSelector } from '../store/hooks';

const IDLE_TIMEOUT = 8 * 60 * 60; // 8h
const CHECK_INTERVAL = 30; // seconds

interface ApplicationOwnProps {
  children?: React.ReactNode;
}

const Application = (props: ApplicationOwnProps & WithTranslation) => {
  const actions = useActions();
  const isSuperuserActive = useAppSelector((store) => store.auth.isSuperuserActive);

  // Idle-logout timer + activity listeners (was componentDidMount). The cleanup
  // clears the interval and removes the listeners, which also keeps it correct
  // under React.StrictMode's double mount in development.
  useEffect(() => {
    let idleSecondsCounter = 0;
    const resetIdle = () => { idleSecondsCounter = 0; };
    document.addEventListener('click', resetIdle);
    document.addEventListener('keypress', resetIdle);
    const timer = window.setInterval(() => {
      idleSecondsCounter += CHECK_INTERVAL;
      if (idleSecondsCounter >= IDLE_TIMEOUT) {
        actions.logout();
      }
    }, CHECK_INTERVAL * 1000);

    return () => {
      clearInterval(timer);
      document.removeEventListener('click', resetIdle);
      document.removeEventListener('keypress', resetIdle);
    };
  }, [actions]);

  // Ends the Auth0 session too; the idle timer above deliberately stays local.
  // preventDefault stops the Header's <a href="/"> racing the SDK redirect.
  const onLogout = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    actions.logoutFederated();
  };

  const onChangeSuperuserActive = () => {
    const next = !isSuperuserActive;
    actions.setSuperuserActive(next);
    // Scope changed → refresh the dashboards list.
    actions.fetchUserDashboards();
  };

  return (
    <React.StrictMode>
      <I18nextProvider i18n={i18n}>
        <div>
          <Header
            onLogout={onLogout}
            onChangeSuperuserActive={onChangeSuperuserActive}
            isSuperuserActive={isSuperuserActive} />
          <section className={styles.content}>
            {props.children}
          </section>
        </div>
      </I18nextProvider>
    </React.StrictMode>
  );
};

export default withTranslation('common')(Application);
